import * as THREE from 'three';
import { VehicleDef, getPaint } from '@voidline/shared';

/**
 * Procedural ship construction. Every hull is lofted from asymmetric cross-section
 * stations (independent upper/lower heights, circle→diamond squash per station),
 * then dressed by a per-silhouette builder — twin prongs, sponsons, ring tails,
 * armour plates, manta winglets, faceted stealth panels — so each craft reads as a
 * different ship at a glance. No model files, no textures.
 */

export interface ShipHandle {
  group: THREE.Group;
  /** Local-space engine nozzle anchors (for trails/flames). */
  engines: THREE.Vector3[];
  /** Hull glow material (emissive accents) — pulsed by effects. */
  glowMat: THREE.MeshBasicMaterial;
  primaryMat: THREE.MeshStandardMaterial;
  /** Optional visual tick — pulses the engine cores. Callers may ignore it. */
  update?(dt: number): void;
  setDamageFlash(v: number): void;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// geometry helpers

interface Station {
  z: number; // fraction 0 (nose) .. 1 (tail)
  w: number; // half-width
  hT: number; // upper half-height
  hB: number; // lower half-height
  y: number; // vertical offset
  squash?: number; // 0 = ellipse, 1 = diamond-ish
}

const st = (z: number, w: number, hT: number, hB: number, y = 0, squash?: number): Station => ({
  z,
  w,
  hT,
  hB,
  y,
  squash,
});

type Pt = [number, number];

/** Loft a closed skin over cross-section stations. Nose at +z/2, tail at -z/2. */
function loft(
  stations: Station[],
  length: number,
  radial: number,
  defSquash: number,
  caps: { nose?: boolean; tail?: boolean } = {}
): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const rows = stations.length;
  for (let si = 0; si < rows; si++) {
    const s = stations[si];
    const squash = s.squash ?? defSquash;
    const z = (0.5 - s.z) * length;
    for (let r = 0; r <= radial; r++) {
      const a = (r / radial) * Math.PI * 2;
      let x = Math.cos(a);
      let y = Math.sin(a);
      // blend circle → diamond
      if (squash > 0) {
        const dx = Math.sign(x) * (1 - Math.abs(Math.sin(a)));
        const dy = Math.sign(y) * (1 - Math.abs(Math.cos(a)));
        x = x * (1 - squash) + dx * squash;
        y = y * (1 - squash) + dy * squash;
      }
      positions.push(x * s.w, y * (y >= 0 ? s.hT : s.hB) + s.y, z);
    }
  }
  for (let si = 0; si < rows - 1; si++) {
    for (let r = 0; r < radial; r++) {
      const a = si * (radial + 1) + r;
      const b = a + radial + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  if (caps.nose) {
    const s = stations[0];
    const ci = positions.length / 3;
    positions.push(0, s.y, (0.5 - s.z) * length);
    for (let r = 0; r < radial; r++) indices.push(ci, r, r + 1);
  }
  if (caps.tail) {
    const s = stations[rows - 1];
    const ci = positions.length / 3;
    positions.push(0, s.y, (0.5 - s.z) * length);
    const base = (rows - 1) * (radial + 1);
    for (let r = 0; r < radial; r++) indices.push(ci, base + r + 1, base + r);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Unindex + flat normals: hard-edged stealth-panel shading. */
function facet(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  const flat = geo.index ? geo.toNonIndexed() : geo;
  if (flat !== geo) geo.dispose();
  flat.computeVertexNormals();
  return flat;
}

/** Mirror a geometry across X with corrected winding (for asymmetric side parts). */
function mirrorX(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo.clone();
  g.scale(-1, 1, 1);
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i += 3) {
    for (let k = 0; k < 3; k++) {
      const tmp = pos.getComponent(i + 1, k);
      pos.setComponent(i + 1, k, pos.getComponent(i + 2, k));
      pos.setComponent(i + 2, k, tmp);
    }
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

/** Vertical blade (fin/strake): points in (z-forward, y-up), thickness across x. */
function finGeo(points: Pt[], thickness: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) shape.lineTo(points[i][0], points[i][1]);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
  geo.rotateY(-Math.PI / 2); // shape x → ship z
  geo.translate(thickness / 2, 0, 0);
  return geo;
}

/** One wing half: points in (span-x, chord-z fwd), root at x=0, thickness on y. */
function wingHalfGeo(points: Pt[], thickness: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) shape.lineTo(points[i][0], points[i][1]);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
  geo.rotateX(Math.PI / 2); // shape y → ship z, thickness → y
  geo.translate(0, thickness / 2, 0);
  return geo;
}

/** Full-span symmetric wing from a right-half outline (one mesh, fuselage hides the seam). */
function symWingGeo(right: Pt[], thickness: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(right[0][0], right[0][1]);
  for (let i = 1; i < right.length; i++) shape.lineTo(right[i][0], right[i][1]);
  for (let i = right.length - 1; i >= 0; i--) shape.lineTo(-right[i][0], right[i][1]);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
  geo.rotateX(Math.PI / 2);
  geo.translate(0, thickness / 2, 0);
  return geo;
}

// ---------------------------------------------------------------------------
// build context + shared dressing

interface Ctx {
  group: THREE.Group;
  primary: THREE.MeshStandardMaterial;
  secondary: THREE.MeshStandardMaterial;
  glow: THREE.MeshBasicMaterial;
  core: THREE.MeshBasicMaterial;
  canopy: THREE.MeshStandardMaterial;
  dark: THREE.MeshStandardMaterial;
  engines: THREE.Vector3[];
  mesh(geo: THREE.BufferGeometry, mat: THREE.Material): THREE.Mesh;
}

function box(
  c: Ctx,
  mat: THREE.Material,
  w: number,
  h: number,
  d: number,
  x: number,
  y: number,
  z: number
): THREE.Mesh {
  const m = c.mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  return m;
}

/** Forward-pointing cone; z is the base position, tip extends toward +z. */
function spike(
  c: Ctx,
  mat: THREE.Material,
  r: number,
  len: number,
  x: number,
  y: number,
  z: number
): THREE.Mesh {
  const geo = new THREE.ConeGeometry(r, len, 8);
  geo.rotateX(Math.PI / 2);
  const m = c.mesh(geo, mat);
  m.position.set(x, y, z + len / 2);
  return m;
}

/** Nozzle shell + dark rim ring + pulsed core disc; registers a trail anchor. */
function addEngine(c: Ctx, x: number, y: number, z: number, r: number, seg = 12, faceted = false): void {
  let nozzleGeo: THREE.BufferGeometry = new THREE.CylinderGeometry(r * 0.82, r * 1.12, 0.42, seg, 1, true);
  nozzleGeo.rotateX(Math.PI / 2);
  if (faceted) nozzleGeo = facet(nozzleGeo);
  const nozzle = c.mesh(nozzleGeo, c.secondary);
  nozzle.position.set(x, y, z);
  let rimGeo: THREE.BufferGeometry = new THREE.TorusGeometry(r * 1.06, r * 0.09, 5, faceted ? 6 : 16);
  if (faceted) rimGeo = facet(rimGeo);
  const rim = c.mesh(rimGeo, c.dark);
  rim.position.set(x, y, z - 0.21);
  const core = c.mesh(new THREE.CircleGeometry(r * 0.8, faceted ? 6 : 12), c.core);
  core.position.set(x, y, z - 0.16);
  core.rotation.y = Math.PI;
  c.engines.push(new THREE.Vector3(x, y, z - 0.24));
}

/** Underslung hover pod with a glow plate protruding beneath, gimballed outward. */
function addHoverPad(c: Ctx, x: number, y: number, z: number, tilt: number): void {
  const pod = box(c, c.secondary, 0.32, 0.12, 0.58, x, y, z);
  pod.rotation.z = tilt;
  const off = 0.06;
  const plate = box(c, c.glow, 0.24, 0.05, 0.46, x + Math.sin(tilt) * off, y - Math.cos(tilt) * off, z);
  plate.rotation.z = tilt;
}

function canopyBubble(c: Ctx, x: number, y: number, z: number, zScale: number, w = 0.75): THREE.Mesh {
  const geo = new THREE.SphereGeometry(0.32, 12, 8);
  geo.scale(w, 0.55, zScale);
  const m = c.mesh(geo, c.canopy);
  m.position.set(x, y, z);
  return m;
}

// ---------------------------------------------------------------------------
// hull builders

/** dart — Kestrel VX: needle nose cap, pitot, shoulder intakes, canards, kinked delta, dorsal fin. */
function buildDart(c: Ctx): void {
  const LEN = 5.2;
  c.mesh(
    loft(
      [
        st(0, 0.05, 0.045, 0.03, 0.1),
        st(0.15, 0.3, 0.2, 0.13, 0.05),
        st(0.42, 0.62, 0.34, 0.22, 0),
        st(0.7, 0.68, 0.36, 0.25, 0),
        st(0.88, 0.52, 0.3, 0.22, 0.02),
        st(1, 0.5, 0.24, 0.2, 0.02),
      ],
      LEN,
      12,
      0.25,
      { tail: true }
    ),
    c.primary
  );
  spike(c, c.secondary, 0.12, 0.6, 0, 0.09, 2.5); // secondary nose cap
  spike(c, c.dark, 0.016, 0.4, 0, 0.09, 3.02); // pitot
  canopyBubble(c, 0, 0.36, 0.9, 1.5);

  // shoulder intake scoops flanking the canopy (dark mouth protrudes from the housing)
  for (const side of [1, -1] as const) {
    box(c, c.secondary, 0.18, 0.12, 0.55, side * 0.52, 0.14, 0.3);
    box(c, c.dark, 0.12, 0.07, 0.2, side * 0.54, 0.15, 0.62);
  }

  // canards
  const canard = c.mesh(
    symWingGeo(
      [
        [0.15, 0.3],
        [0.62, -0.02],
        [0.62, -0.25],
        [0.15, -0.2],
      ],
      0.05
    ),
    c.secondary
  );
  canard.position.set(0, 0.12, 1.66);

  // kinked delta wing (primary) with secondary tip caps + glow stripes
  const wing = c.mesh(
    symWingGeo(
      [
        [0.3, 1.0],
        [1.3, -0.3],
        [1.85, -0.75],
        [1.85, -1.15],
        [1.1, -0.85],
        [0.3, -1.05],
      ],
      0.08
    ),
    c.primary
  );
  wing.position.set(0, 0.02, -0.62);
  for (const side of [1, -1] as const) {
    // cap wraps past the x=1.85 tip face so the two faces never share a plane
    box(c, c.secondary, 0.34, 0.12, 0.44, side * 1.72, 0.02, -1.57);
    box(c, c.glow, 0.16, 0.12, 0.55, side * 1.42, 0.02, -1.27);
  }

  // dorsal fin (base sunk so it stays rooted where the tail slims down)
  const fin = c.mesh(
    finGeo(
      [
        [0.6, -0.12],
        [-0.5, -0.12],
        [-0.9, 0.65],
        [-0.35, 0.58],
      ],
      0.05
    ),
    c.secondary
  );
  fin.position.set(0, 0.3, -1.8);

  for (const side of [1, -1] as const) addEngine(c, side * 0.25, 0.02, -2.62, 0.22);
  for (const side of [1, -1] as const) {
    addHoverPad(c, side * 0.3, -0.14, 1.1, side * 0.3);
    addHoverPad(c, side * 0.5, -0.18, -1.2, side * 0.3);
  }
  box(c, c.glow, 0.05, 0.03, 2.6, 0, 0.365, 0.2); // spine stripe
}

/** hammer — Rampart: ram plow, armour plate ridges, sponsons, skids, mast, visor slit. */
function buildHammer(c: Ctx): void {
  const LEN = 5.6;
  c.mesh(
    loft(
      [
        st(0, 0.6, 0.18, 0.2, 0.02, 0.7),
        st(0.18, 1.0, 0.32, 0.3, 0, 0.7),
        st(0.5, 1.12, 0.42, 0.34, 0, 0.6),
        st(0.82, 0.98, 0.4, 0.3, 0, 0.6),
        st(1, 0.8, 0.34, 0.26, 0, 0.7),
      ],
      LEN,
      12,
      0.65,
      { nose: true, tail: true }
    ),
    c.primary
  );

  // faceted ram plow
  const ram = new THREE.ConeGeometry(0.55, 0.8, 4);
  ram.scale(1.4, 1, 0.55);
  ram.rotateX(Math.PI / 2);
  const plow = c.mesh(facet(ram), c.secondary);
  plow.position.set(0, 0, 3.1);
  for (const side of [1, -1] as const) box(c, c.glow, 0.26, 0.09, 0.07, side * 0.35, -0.1, 2.6); // furnace eyes, embedded in the nose cheeks

  // armour plating ridges, seated on the hull shoulder (per-station surface heights)
  for (const side of [1, -1] as const) {
    const ridges: [number, number][] = [
      [1.3, 0.22],
      [0.2, 0.27],
      [-0.9, 0.26],
    ];
    for (const [pz, py] of ridges) {
      const plate = box(c, c.secondary, 0.52, 0.1, 0.92, side * 0.62, py, pz);
      plate.rotation.z = -side * 0.25;
    }
  }
  box(c, c.secondary, 0.6, 0.12, 0.85, 0, 0.4, 0.9);
  box(c, c.secondary, 0.6, 0.12, 0.85, 0, 0.42, -0.5);

  // side sponsons carrying the outer engines
  const sponsonGeo = loft(
    [
      st(0, 0.08, 0.08, 0.08, 0),
      st(0.3, 0.3, 0.26, 0.26, 0, 0.5),
      st(0.7, 0.32, 0.28, 0.28, 0, 0.5),
      st(1, 0.24, 0.22, 0.22, 0, 0.5),
    ],
    3.0,
    10,
    0.5,
    { tail: true }
  );
  for (const side of [1, -1] as const) {
    const pod = c.mesh(side === 1 ? sponsonGeo : sponsonGeo.clone(), c.primary);
    pod.position.set(side * 1.15, -0.06, -0.5);
    addEngine(c, side * 1.15, -0.06, -2.1, 0.26);
  }
  addEngine(c, 0, 0, -2.85, 0.3);

  // stub fins, skids, mast, visor
  const finG = finGeo(
    [
      [0.5, 0],
      [-0.4, 0],
      [-0.7, 0.5],
      [-0.25, 0.45],
    ],
    0.07
  );
  for (const side of [1, -1] as const) {
    const fin = c.mesh(side === 1 ? finG : finG.clone(), c.secondary);
    fin.position.set(side * 0.55, 0.18, -2.0);
    fin.rotation.z = -side * 0.35;
  }
  for (const side of [1, -1] as const) {
    box(c, c.dark, 0.3, 0.14, 2.6, side * 0.68, -0.42, -0.3);
    box(c, c.glow, 0.2, 0.06, 2.3, side * 0.68, -0.5, -0.3);
  }
  const mast = c.mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.7, 5), c.dark);
  mast.position.set(-0.4, 0.55, 1.1);
  box(c, c.glow, 0.06, 0.06, 0.06, -0.4, 0.9, 1.1);
  const visor = canopyBubble(c, 0, 0.36, 1.6, 0.8, 1.15);
  visor.scale.y = 0.55; // squashed slit
}

/** quill — NIX-7: needle spine, tip beacon, raked antennae, dihedral wings, ring tail. */
function buildQuill(c: Ctx): void {
  const LEN = 4.6;
  c.mesh(
    loft(
      [
        st(0, 0.06, 0.06, 0.05, 0),
        st(0.28, 0.3, 0.28, 0.24, 0),
        st(0.58, 0.48, 0.4, 0.32, 0),
        st(0.85, 0.4, 0.34, 0.26, 0),
        st(1, 0.18, 0.18, 0.16, 0.04),
      ],
      LEN,
      10,
      0,
      { tail: true }
    ),
    c.primary
  );

  // needle spine + sleeve + beacon
  spike(c, c.dark, 0.045, 1.6, 0, 0, 2.2);
  spike(c, c.secondary, 0.09, 0.5, 0, 0, 2.2);
  const bead = c.mesh(new THREE.SphereGeometry(0.05, 6, 4), c.glow);
  bead.position.set(0, 0, 3.82);
  canopyBubble(c, 0, 0.36, 0.46, 1.0, 0.65);

  // high-dihedral wings with glow tips
  const half = wingHalfGeo(
    [
      [0, 0.55],
      [1.1, 0.05],
      [1.1, -0.25],
      [0, -0.45],
    ],
    0.06
  );
  for (const side of [1, -1] as const) {
    const w = c.mesh(side === 1 ? half : mirrorX(half), c.secondary);
    w.position.set(side * 0.3, 0.15, -0.23);
    w.rotation.z = side * 0.4;
    const tip = c.mesh(new THREE.BoxGeometry(0.14, 0.12, 0.34), c.glow);
    w.add(tip);
    tip.position.set(side * 1.04, 0, -0.1);
  }

  // raked antenna pair
  for (const side of [1, -1] as const) {
    const ant = c.mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.8, 5), c.dark);
    ant.position.set(side * 0.14, 0.65, -1.56);
    ant.rotation.x = -0.7;
  }

  // ring stabilizer around the single engine
  const ring = c.mesh(new THREE.TorusGeometry(0.62, 0.055, 6, 20), c.secondary);
  ring.position.set(0, 0.05, -2.2);
  for (const a of [Math.PI / 2, Math.PI * (7 / 6), Math.PI * (11 / 6)]) {
    const strut = box(c, c.dark, 0.05, 0.6, 0.08, Math.cos(a) * 0.35, 0.05 + Math.sin(a) * 0.35, -2.2);
    strut.rotation.z = a - Math.PI / 2;
  }
  addEngine(c, 0, 0.05, -2.42, 0.3);

  box(c, c.secondary, 0.28, 0.08, 1.3, 0, -0.32, -0.2); // belly plate
  for (const side of [1, -1] as const) {
    addHoverPad(c, side * 0.24, -0.21, 0.9, side * 0.35);
    addHoverPad(c, side * 0.3, -0.27, -0.9, side * 0.35);
  }
  box(c, c.glow, 0.04, 0.025, 1.5, 0, 0.4, -0.35);
}

/** blade — Sableline: flat chisel body, chine glow, anhedral blades, split tail, airbrakes. */
function buildBlade(c: Ctx): void {
  const LEN = 5.4;
  c.mesh(
    loft(
      [
        st(0, 0.12, 0.035, 0.03, 0.06, 1),
        st(0.22, 0.6, 0.14, 0.1, 0.03, 1),
        st(0.5, 0.9, 0.26, 0.18, 0, 0.85),
        st(0.78, 0.78, 0.3, 0.2, 0, 0.85),
        st(1, 0.5, 0.24, 0.16, 0.03, 1),
      ],
      LEN,
      12,
      0.9,
      { tail: true }
    ),
    c.primary
  );

  // chisel nose cap
  const chisel = new THREE.ConeGeometry(0.12, 0.6, 8);
  chisel.scale(2.2, 1, 0.4);
  chisel.rotateX(Math.PI / 2);
  const nose = c.mesh(chisel, c.secondary);
  nose.position.set(0, 0.06, 2.92);
  canopyBubble(c, 0, 0.22, 0.7, 1.3, 0.6);

  // chine glow lines
  for (const side of [1, -1] as const) box(c, c.glow, 0.05, 0.05, 1.2, side * 0.88, 0, -0.1);

  // anhedral blade wings with wrapped glow tips
  const half = wingHalfGeo(
    [
      [0, 1.2],
      [1.15, -0.55],
      [1.15, -1.0],
      [0, -1.3],
    ],
    0.07
  );
  for (const side of [1, -1] as const) {
    const w = c.mesh(side === 1 ? half : mirrorX(half), c.secondary);
    w.position.set(side * 0.3, 0.1, -0.55);
    w.rotation.z = -side * 0.35;
    const tip = c.mesh(new THREE.BoxGeometry(0.14, 0.12, 0.45), c.glow);
    w.add(tip);
    tip.position.set(side * 1.08, 0, -0.77);
  }

  // split tail (outward-canted twin fins) + popped airbrake flaps
  const finG = finGeo(
    [
      [0.55, 0],
      [-0.45, 0],
      [-0.8, 0.6],
      [-0.3, 0.55],
    ],
    0.05
  );
  for (const side of [1, -1] as const) {
    const fin = c.mesh(side === 1 ? finG : finG.clone(), c.secondary);
    fin.position.set(side * 0.4, 0.1, -1.95);
    fin.rotation.z = -side * 0.5;
    const flap = box(c, c.secondary, 0.5, 0.04, 0.66, side * 0.5, 0.15, -1.35);
    flap.rotation.y = side * 0.18;
    flap.rotation.z = -side * 0.1;
  }

  for (const side of [1, -1] as const) addEngine(c, side * 0.28, 0.04, -2.75, 0.22);
  for (const side of [1, -1] as const) {
    addHoverPad(c, side * 0.3, -0.08, 1.3, side * 0.3);
    addHoverPad(c, side * 0.3, -0.19, -1.3, side * 0.3);
  }
}

/** wing — Aurelion GT: glider fuselage, collar band, straight wing, tip nacelles + fins. */
function buildWing(c: Ctx): void {
  const LEN = 4.8;
  c.mesh(
    loft(
      [
        st(0, 0.07, 0.05, 0.04, 0.02),
        st(0.2, 0.3, 0.2, 0.16, 0),
        st(0.5, 0.48, 0.3, 0.24, 0),
        st(0.8, 0.42, 0.28, 0.2, 0),
        st(1, 0.3, 0.2, 0.16, 0.02),
      ],
      LEN,
      12,
      0.3,
      { tail: true }
    ),
    c.primary
  );
  spike(c, c.secondary, 0.1, 0.5, 0, 0.03, 2.32);
  spike(c, c.dark, 0.015, 0.45, 0, 0.03, 2.78);
  canopyBubble(c, 0, 0.28, 0.85, 1.2, 0.7);

  // secondary collar band around the fuselage
  const collar = c.mesh(new THREE.TorusGeometry(1, 0.05, 6, 22), c.secondary);
  collar.position.set(0, 0, 0.4);
  collar.scale.set(0.46, 0.3, 1);

  // straight main wing with mid-span glow strips
  const wing = c.mesh(
    symWingGeo(
      [
        [0.3, 0.55],
        [1.55, 0.25],
        [1.55, -0.45],
        [0.3, -0.75],
      ],
      0.1
    ),
    c.secondary
  );
  wing.position.set(0, 0.05, -0.05);
  for (const side of [1, -1] as const) box(c, c.glow, 1.0, 0.14, 0.08, side * 0.95, 0.05, 0.3);

  // wingtip nacelles with engines and tip fins
  const nacGeo = loft(
    [
      st(0, 0.05, 0.05, 0.05, 0),
      st(0.25, 0.16, 0.16, 0.16, 0),
      st(0.7, 0.17, 0.17, 0.17, 0),
      st(1, 0.11, 0.11, 0.11, 0),
    ],
    2.2,
    10,
    0,
    { tail: true }
  );
  const tipFinG = finGeo(
    [
      [0.45, 0],
      [-0.35, 0],
      [-0.65, 0.42],
      [-0.15, 0.38],
    ],
    0.04
  );
  for (const side of [1, -1] as const) {
    const nac = c.mesh(side === 1 ? nacGeo : nacGeo.clone(), c.primary);
    nac.position.set(side * 1.55, 0.05, -0.15);
    const fin = c.mesh(side === 1 ? tipFinG : tipFinG.clone(), c.secondary);
    fin.position.set(side * 1.55, 0.2, -0.7);
    addEngine(c, side * 1.55, 0.05, -1.32, 0.17);
  }
  addEngine(c, 0, 0.02, -2.45, 0.18);

  // ventral strake
  const strake = c.mesh(
    finGeo(
      [
        [0.55, 0],
        [-0.4, 0],
        [-0.65, -0.42],
        [-0.2, -0.38],
      ],
      0.04
    ),
    c.secondary
  );
  strake.position.set(0, -0.18, -1.7);

  for (const side of [1, -1] as const) {
    addHoverPad(c, side * 0.24, -0.16, 1.0, side * 0.25);
    addHoverPad(c, side * 0.24, -0.2, -1.0, side * 0.25);
  }
  box(c, c.glow, 0.04, 0.03, 2.2, 0, 0.295, 0.3);
}

/** fork — Pyre Falcon: two real lofted prongs, energy bridge, big twin burners. */
function buildFork(c: Ctx): void {
  // rear body (blunt front, prongs plug into its flanks)
  const body = c.mesh(
    loft(
      [
        st(0, 0.55, 0.3, 0.26, 0, 0.4),
        st(0.25, 0.78, 0.42, 0.34, 0, 0.4),
        st(0.6, 0.82, 0.46, 0.36, 0, 0.45),
        st(0.85, 0.72, 0.42, 0.32, 0),
        st(1, 0.7, 0.34, 0.26, 0),
      ],
      3.6,
      12,
      0.4,
      { nose: true, tail: true }
    ),
    c.primary
  );
  body.position.z = -0.95;

  // twin lofted prongs with secondary tip cones and inner glow beads
  const prongGeo = loft(
    [
      st(0, 0.05, 0.05, 0.04, 0),
      st(0.2, 0.16, 0.15, 0.12, 0),
      st(0.55, 0.24, 0.22, 0.18, 0),
      st(1, 0.26, 0.24, 0.2, 0),
    ],
    3.4,
    10,
    0.35
  );
  for (const side of [1, -1] as const) {
    const prong = c.mesh(side === 1 ? prongGeo : prongGeo.clone(), c.primary);
    prong.position.set(side * 0.58, 0.05, 0.95);
    spike(c, c.secondary, 0.1, 0.45, side * 0.58, 0.05, 2.6);
    box(c, c.glow, 0.06, 0.06, 0.5, side * 0.38, 0.05, 1.6); // glow strip half-sunk in the prong's inner wall
  }

  // energy bridge between the prongs
  box(c, c.dark, 0.78, 0.16, 0.2, 0, 0.02, 0.95);
  box(c, c.glow, 0.75, 0.07, 0.13, 0, 0.05, 1.16);

  canopyBubble(c, 0, 0.44, 0, 1.4, 0.8);

  // swept wings with glow tips
  const wing = c.mesh(
    symWingGeo(
      [
        [0.5, 0.1],
        [1.3, -0.7],
        [1.3, -1.0],
        [0.5, -0.65],
      ],
      0.08
    ),
    c.secondary
  );
  wing.position.set(0, 0.12, -1.0);
  for (const side of [1, -1] as const) box(c, c.glow, 0.16, 0.13, 0.28, side * 1.2, 0.12, -1.85);

  // tall single fin with beacon (base sunk into the tapering spine)
  const fin = c.mesh(
    finGeo(
      [
        [0.8, -0.12],
        [-0.7, -0.12],
        [-1.15, 0.85],
        [-0.5, 0.8],
      ],
      0.06
    ),
    c.secondary
  );
  fin.position.set(0, 0.42, -1.7);
  box(c, c.glow, 0.06, 0.08, 0.4, 0, 1.24, -2.72);

  for (const side of [1, -1] as const) addEngine(c, side * 0.42, 0.02, -2.8, 0.32);
  addEngine(c, 0, -0.2, -2.8, 0.15);
  for (const side of [1, -1] as const) {
    addHoverPad(c, side * 0.58, -0.13, 1.7, side * 0.3);
    addHoverPad(c, side * 0.6, -0.21, -1.6, side * 0.3);
  }
  box(c, c.glow, 0.06, 0.03, 1.6, 0, 0.465, -1.0);
}

/** ray — Glacio S/A: manta blanket body, drooping curved winglets, gill slits, keel. */
function buildRay(c: Ctx): void {
  const LEN = 4.9;
  c.mesh(
    loft(
      [
        st(0, 0.18, 0.06, 0.05, 0.03, 0.35),
        st(0.2, 0.75, 0.18, 0.12, 0, 0.3),
        st(0.45, 1.3, 0.26, 0.16, 0, 0.3),
        st(0.65, 1.35, 0.24, 0.15, 0, 0.35),
        st(0.85, 0.9, 0.2, 0.13, 0, 0.4),
        st(1, 0.45, 0.16, 0.1, 0.04, 0.5),
      ],
      LEN,
      14,
      0.3,
      { tail: true }
    ),
    c.primary
  );
  canopyBubble(c, 0, 0.24, 1.0, 1.4, 0.85);

  // drooping curved winglets, swept back, with glow tips
  const wl = new THREE.Shape();
  wl.moveTo(0, 0.03);
  wl.quadraticCurveTo(0.55, -0.02, 0.95, -0.5);
  wl.lineTo(0.86, -0.56);
  wl.quadraticCurveTo(0.5, -0.12, 0, -0.07);
  wl.closePath();
  const wlGeo = new THREE.ExtrudeGeometry(wl, { depth: 0.8, bevelEnabled: false });
  wlGeo.translate(0, 0, -0.4);
  for (const side of [1, -1] as const) {
    const tipWing = c.mesh(side === 1 ? wlGeo : mirrorX(wlGeo), c.secondary);
    tipWing.position.set(side * 1.26, 0.08, -0.35);
    tipWing.rotation.y = side * 0.25;
    const tip = c.mesh(new THREE.BoxGeometry(0.1, 0.1, 0.3), c.glow);
    tipWing.add(tip);
    tip.position.set(side * 0.88, -0.5, 0);
  }

  // leading-edge glow, two segments per side following the sweep
  for (const side of [1, -1] as const) {
    const segA = box(c, c.glow, 0.05, 0.05, 1.0, side * 0.465, 0.03, 1.96);
    segA.rotation.y = -side * 0.53;
    const segB = box(c, c.glow, 0.05, 0.05, 1.25, side * 1.02, 0.02, 0.86);
    segB.rotation.y = -side * 0.42;
  }

  // gill glow slits on the shoulders
  for (const side of [1, -1] as const) {
    const gills: [number, number, number][] = [
      [0.6, 0.19, 0.4],
      [0.75, 0.17, 0.18],
      [0.9, 0.15, -0.04],
    ];
    for (const [gx, gy, gz] of gills) {
      const gill = box(c, c.glow, 0.34, 0.03, 0.07, side * gx, gy, gz);
      gill.rotation.y = side * 0.5;
      gill.rotation.z = -side * 0.15;
    }
  }

  // belly keel + canted tail fins
  box(c, c.secondary, 0.34, 0.12, 1.9, 0, -0.18, -0.6);
  const finG = finGeo(
    [
      [0.45, 0],
      [-0.35, 0],
      [-0.6, 0.42],
      [-0.15, 0.38],
    ],
    0.04
  );
  for (const side of [1, -1] as const) {
    const fin = c.mesh(side === 1 ? finG : finG.clone(), c.secondary);
    fin.position.set(side * 0.32, 0.12, -1.95);
    fin.rotation.z = -side * 0.45;
  }

  for (const side of [1, -1] as const) addEngine(c, side * 0.5, -0.02, -2.5, 0.22);
  for (const side of [1, -1] as const) {
    addHoverPad(c, side * 0.7, -0.12, 0.9, side * 0.35);
    addHoverPad(c, side * 0.7, -0.13, -0.9, side * 0.35);
  }
}

/** prism — Vantablade: faceted stealth panels, extreme anhedral, tall raked fin. */
function buildPrism(c: Ctx): void {
  const LEN = 5.8;
  c.mesh(
    facet(
      loft(
        [
          st(0, 0.04, 0.07, 0.05, 0),
          st(0.25, 0.34, 0.38, 0.26, 0),
          st(0.55, 0.58, 0.48, 0.3, 0),
          st(0.8, 0.46, 0.4, 0.26, 0),
          st(1, 0.26, 0.26, 0.18, 0),
        ],
        LEN,
        6,
        0.45,
        { tail: true }
      )
    ),
    c.primary
  );
  spike(c, c.dark, 0.012, 0.55, 0, 0, 2.9);

  // faceted gem canopy
  const gem = new THREE.SphereGeometry(0.3, 5, 3);
  gem.scale(1.0, 0.55, 1.9);
  const cano = c.mesh(facet(gem), c.canopy);
  cano.position.set(0, 0.4, 0.75);

  // extreme anhedral blade wings with glow tip blades
  const half = wingHalfGeo(
    [
      [0, 0.8],
      [1.3, -0.55],
      [1.3, -0.85],
      [0, -0.9],
    ],
    0.05
  );
  for (const side of [1, -1] as const) {
    const w = c.mesh(side === 1 ? half : mirrorX(half), c.secondary);
    w.position.set(side * 0.25, 0.1, -1.04);
    w.rotation.z = -side * 0.55;
    const tip = c.mesh(new THREE.BoxGeometry(0.12, 0.11, 0.36), c.glow);
    w.add(tip);
    tip.position.set(side * 1.24, 0, -0.7);
  }

  // tall raked fin (base sunk past the tail taper) + ventral fin
  const fin = c.mesh(
    finGeo(
      [
        [0.9, -0.15],
        [-0.6, -0.15],
        [-1.3, 1.0],
        [-0.7, 0.92],
      ],
      0.05
    ),
    c.secondary
  );
  fin.position.set(0, 0.34, -1.85);
  const ventral = c.mesh(
    finGeo(
      [
        [0.5, 0],
        [-0.35, 0],
        [-0.7, -0.5],
        [-0.25, -0.45],
      ],
      0.05
    ),
    c.secondary
  );
  ventral.position.set(0, -0.22, -2.1);

  // chine glow lines + tail slit
  for (const side of [1, -1] as const) box(c, c.glow, 0.05, 0.05, 1.4, side * 0.5, 0, -0.35);
  box(c, c.glow, 0.44, 0.05, 0.08, 0, 0.18, -2.92); // straddles the tail cap so no shared plane

  addEngine(c, 0, 0.04, -2.95, 0.3, 6, true);
  for (const side of [1, -1] as const) {
    addHoverPad(c, side * 0.22, -0.16, 1.2, side * 0.3);
    addHoverPad(c, side * 0.22, -0.21, -1.2, side * 0.3);
  }
}

const HULLS: Record<VehicleDef['hull'], (c: Ctx) => void> = {
  dart: buildDart,
  hammer: buildHammer,
  quill: buildQuill,
  blade: buildBlade,
  wing: buildWing,
  fork: buildFork,
  ray: buildRay,
  prism: buildPrism,
};

/** Engine-core pulse rate per hull (Hz) — heavies idle slow, sprinters flutter. */
const PULSE_HZ: Record<VehicleDef['hull'], number> = {
  dart: 1.6,
  hammer: 0.9,
  quill: 2.8,
  blade: 1.8,
  wing: 1.4,
  fork: 2.4,
  ray: 1.1,
  prism: 2.0,
};

// ---------------------------------------------------------------------------

export function buildShip(vehicle: VehicleDef, paintId: string): ShipHandle {
  const paint = getPaint(vehicle, paintId === 'bot' ? vehicle.paints[0].id : paintId);
  const group = new THREE.Group();
  const disposables: { dispose(): void }[] = [];

  const primaryMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(paint.primary),
    roughness: 0.38,
    metalness: 0.75,
  });
  const secondaryMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(paint.secondary),
    roughness: 0.5,
    metalness: 0.6,
  });
  const glowMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(paint.glow), toneMapped: false });
  const glowColor = new THREE.Color(paint.glow);
  const coreMat = new THREE.MeshBasicMaterial({
    color: glowColor.clone().multiplyScalar(1.35),
    toneMapped: false,
  });
  const canopyMat = new THREE.MeshStandardMaterial({
    color: 0x0a1018,
    roughness: 0.05,
    metalness: 0.95,
  });
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x181b22, roughness: 0.55, metalness: 0.55 });
  disposables.push(primaryMat, secondaryMat, glowMat, coreMat, canopyMat, darkMat);

  const ctx: Ctx = {
    group,
    primary: primaryMat,
    secondary: secondaryMat,
    glow: glowMat,
    core: coreMat,
    canopy: canopyMat,
    dark: darkMat,
    engines: [],
    mesh(geo, mat) {
      disposables.push(geo);
      const m = new THREE.Mesh(geo, mat);
      group.add(m);
      return m;
    },
  };
  (HULLS[vehicle.hull] ?? HULLS.dart)(ctx);

  const pulseHz = PULSE_HZ[vehicle.hull] ?? 1.6;
  let pulseT = Math.random() * 20; // desync the grid
  let flash = 0;
  return {
    group,
    engines: ctx.engines,
    glowMat,
    primaryMat,
    update(dt: number) {
      pulseT += dt;
      const p = 1.1 + 0.5 * (0.5 + 0.5 * Math.sin(pulseT * pulseHz * Math.PI * 2));
      coreMat.color.copy(glowColor).multiplyScalar(p);
    },
    setDamageFlash(v: number) {
      if (v === flash) return;
      flash = v;
      primaryMat.emissive.setRGB(v * 0.8, v * 0.25, v * 0.1);
      primaryMat.emissiveIntensity = 1;
    },
    dispose() {
      group.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
